import { POSTHOG_SCHEMA_VERSION } from '@/lib/posthogIdentity';
import { trackEvent } from '@/lib/trackEvent';

type LinkClickOptions = {
  reportId: string;
  root?: ParentNode;
};

const getDestinationDomain = (url: string): string => {
  try {
    const parsed = new URL(url, window.location.href);
    if (parsed.hostname) return parsed.hostname;

    if (parsed.protocol === 'mailto:') {
      const email = decodeURIComponent(
        `${parsed.pathname}${parsed.search}`.replace(/^\/*/, '')
      )
        .split('?')[0]
        .trim();
      const at = email.lastIndexOf('@');
      if (at >= 0 && at < email.length - 1) {
        return email.slice(at + 1).toLowerCase();
      }
    }

    return parsed.protocol.replace(/:$/, '');
  } catch {
    return '';
  }
};

const getLinkText = (anchor: HTMLAnchorElement): string => {
  const text = (anchor.textContent || '').replace(/\s+/g, ' ').trim();
  if (text) return text.slice(0, 200);
  return (anchor.getAttribute('aria-label') || anchor.href || '').slice(0, 200);
};

const resolveSectionElement = (anchor: HTMLAnchorElement): HTMLElement | null =>
  anchor.closest('[data-report-section]') as HTMLElement | null;

const resolveSectionName = (section: HTMLElement | null): string =>
  section?.dataset.reportSectionName ||
  section?.dataset.reportSection ||
  'unknown';

const resolveSpecialName = (
  anchor: HTMLAnchorElement,
  section: HTMLElement | null
): string | undefined => {
  const nearest = anchor.closest(
    '[data-report-special-name]'
  ) as HTMLElement | null;
  return (
    nearest?.dataset.reportSpecialName ||
    section?.dataset.reportSpecialName ||
    undefined
  );
};

const resolveLegacyLinkEventName = (
  anchor: HTMLAnchorElement,
  section: HTMLElement | null
): string | undefined => {
  const box = anchor.closest('[data-report-box]') as HTMLElement | null;
  if (box) return 'box_link_clicked';
  return (
    section?.dataset.reportLinkEvent ||
    anchor.dataset.reportLinkEvent ||
    undefined
  );
};

const isTrackableHref = (href: string): boolean => {
  const value = href.trim().toLowerCase();
  return value.length > 0 && !value.startsWith('javascript:');
};

const collectTrackableLinks = (
  scope: ParentNode | null
): HTMLAnchorElement[] => {
  if (!scope) return [];
  return Array.from(scope.querySelectorAll<HTMLAnchorElement>('a[href]')).filter(
    (link) => isTrackableHref(link.getAttribute('href') || '')
  );
};

const resolveLinkPosition = (
  anchor: HTMLAnchorElement,
  box: HTMLElement | null,
  section: HTMLElement | null
): number => {
  const scope = box || section;
  const links = scope ? collectTrackableLinks(scope) : [anchor];
  const index = links.indexOf(anchor);
  return Math.max(1, index + 1);
};

export const startSectionLinkClickTracking = (
  options: LinkClickOptions
): (() => void) => {
  if (typeof window === 'undefined' || !options.reportId) {
    return () => undefined;
  }

  const root = options.root ?? document;
  const reportId = options.reportId;

  const handleClick = (event: Event) => {
    const target = event.target as HTMLElement | null;
    const anchor = target?.closest?.('a[href]') as HTMLAnchorElement | null;
    if (!anchor || !root.contains(anchor)) return;
    if (!isTrackableHref(anchor.getAttribute('href') || '')) return;

    const destinationUrl = anchor.href;
    const destinationDomain = getDestinationDomain(destinationUrl);
    const section = resolveSectionElement(anchor);
    const sectionName = resolveSectionName(section);
    const box = anchor.closest('[data-report-box]') as HTMLElement | null;
    const specialName = resolveSpecialName(anchor, section);
    const linkPosition = resolveLinkPosition(anchor, box, section);

    const linkClickedProperties: Record<string, unknown> = {
      report_id: reportId,
      schema_version: POSTHOG_SCHEMA_VERSION,
      section_name: sectionName,
      link_position: linkPosition,
      destination_url: destinationUrl,
      destination_domain: destinationDomain,
    };

    if (specialName) linkClickedProperties.special_name = specialName;
    if (box) linkClickedProperties.container = 'box';

    trackEvent('link_clicked', linkClickedProperties, {
      transport: 'sendBeacon',
    });

    const legacyEventName = resolveLegacyLinkEventName(anchor, section);
    if (!legacyEventName) return;

    const legacyProperties: Record<string, unknown> = {
      report_id: reportId,
      schema_version: POSTHOG_SCHEMA_VERSION,
      link_position: linkPosition,
      destination_url: destinationUrl,
      link_text: getLinkText(anchor),
    };

    if (destinationDomain) legacyProperties.destination_domain = destinationDomain;
    if (sectionName) legacyProperties.section_name = sectionName;
    if (specialName) legacyProperties.special_name = specialName;
    if (box) legacyProperties.container = 'box';

    trackEvent(legacyEventName, legacyProperties, {
      transport: 'sendBeacon',
    });
  };

  root.addEventListener('click', handleClick, true);

  return () => {
    root.removeEventListener('click', handleClick, true);
  };
};
